// src/js/core/copy.js
//
// クライアント側の文言取得ヘルパ。IMPLEMENTATION.md 8章「サイトに表示される文言を
// コードに直接書かない」規約に従い、Web Components はハードコードされた日本語文字列を
// 持たず、必ず copy('キー') 経由で文言を取得する。
//
// 初期HTMLに埋め込まれた文言（44_マイクロコピー_文言設計.md 25.3節の
// <script type="application/json" id="xte-copy-data"> ）をまず読み、
// そこに存在しないキーが参照された場合のみ GET /xte/v1/copy から補う
// （常時フル取得はせず、埋め込み済みで足りている限り追加リクエストを発生させない）。

const SCRIPT_ELEMENT_ID = 'xte-copy-data';
const COPY_ENDPOINT = '/wp-json/xte/v1/copy';
const MISSING_KEY_REFETCH_DEBOUNCE_MS = 200;

let copyStore = primeFromEmbeddedScript();
let refreshPromise = null;
let missingKeyTimer = null;
const pendingMissingKeys = new Set();

function primeFromEmbeddedScript() {
  if (typeof document === 'undefined') return {};
  const el = document.getElementById(SCRIPT_ELEMENT_ID);
  if (!el) return {};
  try {
    const parsed = JSON.parse(el.textContent ?? '{}');
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * 文言を取得する。`{変数名}` プレースホルダを vars で置換する
 * （44文書24章: 文字列連結ではなくプレースホルダ方式に統一する）。
 *
 * @param {string} key `xte_copy_` 接頭辞を含む完全なキー
 * @param {Record<string, string|number>} [vars]
 * @returns {string}
 */
export function copy(key, vars = {}) {
  const template = copyStore[key];

  if (template === undefined) {
    scheduleMissingKeyRefetch(key);
    // 未定義キー: 開発中に気付けるよう生キーをそのまま返す
    // （44文書 実装チェックリスト: 本番でユーザーに [[key]] が見えたまま放置されないよう
    // xte/copy/missing_key 相当の検知をJS側でも行う。ここでは console.warn + 自動再取得が担う）。
    console.warn(`[copy] 未定義の文言キーです: ${key}`);
    return `[[${key}]]`;
  }

  let text = template;
  for (const [name, value] of Object.entries(vars)) {
    text = text.replaceAll(`{${name}}`, String(value));
  }
  return text;
}

function scheduleMissingKeyRefetch(key) {
  pendingMissingKeys.add(key);
  if (missingKeyTimer) return;
  missingKeyTimer = setTimeout(() => {
    missingKeyTimer = null;
    pendingMissingKeys.clear();
    // 個別キーの部分取得APIは持たないため（32文書に個別キー取得の定義がない）、
    // 不足が検知された時点でセット全体を再取得する。デバウンスにより、
    // 同一tick付近で複数の未定義キーが参照されても1回のリクエストにまとめる。
    refreshCopyStore().catch(() => {});
  }, MISSING_KEY_REFETCH_DEBOUNCE_MS);
}

/**
 * GET /xte/v1/copy を叩いて文言セット全体を再取得する。
 * `settings_version` の差分検知ポーリング（main.js）から呼ばれた場合、
 * 管理画面での文言上書き（44文書25.3節）を反映する経路にもなる。
 *
 * @returns {Promise<void>}
 */
export async function refreshCopyStore() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(COPY_ENDPOINT, { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      if (data && typeof data === 'object') {
        copyStore = data;
        document.dispatchEvent(new CustomEvent('xte:copy-updated'));
      }
    } catch (err) {
      console.warn('[copy] 文言セットの再取得に失敗しました:', err);
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** 現在のキー数など、診断目的の最小限の情報を返す。 */
export function getCopyStoreSize() {
  return Object.keys(copyStore).length;
}
